import { useCallback, useContext } from "react";
import {
  Board,
  BoardMachinesStates,
  quoteLoaded,
  QuoteState,
  updateQuoteState,
} from "../../../../../reducers/uploadedBoard";
import { useRefreshBoardInStore } from "../boardRefreshes";
import { useAppDispatch } from "../../../store";
import { BoardIdContext, BoardShipmentIds } from "./reviewBoardFlowLoader";
import { useSWRConfig } from "swr";
import { quoteHistoryUrl } from "../quoteHistory";

export default function useRefreshQuote() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();
  const updateQuote = useCallback(
    (board: Board) => {
      dispatch(quoteLoaded({ board }));
    },
    [dispatch],
  );
  const { refreshBoard: refreshQuote } = useRefreshBoardInStore(updateQuote);
  const { refreshBoard } = useRefreshBoardInStore();
  const { mutate } = useSWRConfig();

  const handleQuoteStateUpdates = useCallback(
    (nextStatesData: BoardMachinesStates, states: BoardMachinesStates) => {
      // The loader calls this for every websocket message from any machine,
      // and twice per message, so only act when the quote itself changed.
      // quote_datetime_updated is bumped on every quote transition, including
      // ones that stay in QUOTED such as custom quote requests and item edits.
      const quoteChanged =
        nextStatesData.quote_state != states.quote_state ||
        nextStatesData.quote_datetime_updated != states.quote_datetime_updated;
      if (!quoteChanged) {
        return;
      }
      // Setting a custom quote or adding to the cart writes a history record
      // during the transition. The global mutate only refetches while a
      // history hook for this board is mounted, so customers never hit the
      // engineer-only endpoint.
      void mutate(quoteHistoryUrl(boardId));
      if (nextStatesData.quote_state == QuoteState.QUOTED) {
        refreshQuote();
      } else if (nextStatesData.quote_state == QuoteState.ORDER_RECEIVED) {
        refreshBoard();
      } else if (boardId) {
        dispatch(
          updateQuoteState({ boardId, quoteState: nextStatesData.quote_state }),
        );
      }
    },
    [boardId, refreshQuote, refreshBoard, dispatch, mutate],
  );

  return {
    refreshQuote,
    handleQuoteStateUpdates,
  };
}
